/**
 * Product Finder Quiz
 * Multi-step quiz that recommends products based on goal, method, and strength.
 */
class ProductFinder extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.selections = { goal: null, method: null, methodLabel: null, strength: null, strengthTag: null };
    this.skipStrength = this.hasAttribute('data-skip-strength');
    this.totalSteps = this.skipStrength ? 2 : 3;
  }

  connectedCallback() {
    this.bindEvents();
    this.updateProgress();
  }

  bindEvents() {
    // Option card selections
    this.querySelectorAll('[data-pf-select]').forEach(btn => {
      btn.addEventListener('click', () => this.handleSelect(btn));
    });

    // Back buttons
    this.querySelectorAll('[data-pf-back]').forEach(btn => {
      btn.addEventListener('click', () => this.goBack());
    });

    // Restart button
    const restart = this.querySelector('[data-pf-restart]');
    if (restart) restart.addEventListener('click', () => this.restart());
  }

  handleSelect(btn) {
    const type = btn.dataset.pfSelect;
    const value = btn.dataset.value;

    // Store selection
    this.selections[type] = value;

    if (type === 'method') {
      this.selections.methodLabel = btn.dataset.label || value;
    }

    if (type === 'strength') {
      this.selections.strengthTag = btn.dataset.tag || null;
    }

    // Visual feedback — highlight selected card
    btn.closest('.he-pf__options').querySelectorAll('.he-pf__card').forEach(c => {
      c.classList.remove('he-pf__card--selected');
    });
    btn.classList.add('he-pf__card--selected');

    // Short delay for visual feedback then advance
    setTimeout(() => this.advance(type), 200);
  }

  advance(type) {
    if (type === 'goal') {
      this.goToStep(1);
    } else if (type === 'method') {
      if (this.skipStrength) {
        this.showResults();
      } else {
        this.goToStep(2);
      }
    } else if (type === 'strength') {
      this.showResults();
    }
  }

  goToStep(step) {
    // Hide all steps
    this.querySelectorAll('[data-pf-step]').forEach(el => {
      el.classList.remove('he-pf__step--active');
      el.style.display = 'none';
    });

    // Show target step
    const target = this.querySelector(`[data-pf-step="${step}"]`);
    if (target) {
      target.style.display = '';
      // Trigger reflow for animation
      void target.offsetHeight;
      target.classList.add('he-pf__step--active');
    }

    this.currentStep = step;
    this.updateProgress();
  }

  goBack() {
    if (this.currentStep === 'results') {
      const prevStep = this.skipStrength ? 1 : 2;
      this.goToStep(prevStep);
      return;
    }
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }

  updateProgress() {
    const stepNum = this.currentStep === 'results' ? this.totalSteps : this.currentStep;
    const pct = this.currentStep === 'results' ? 100 : ((stepNum + 1) / this.totalSteps) * 100;

    const fill = this.querySelector('[data-pf-progress]');
    if (fill) fill.style.width = `${pct}%`;

    // Update step dots
    this.querySelectorAll('[data-pf-step-dot]').forEach(dot => {
      const dotStep = parseInt(dot.dataset.pfStepDot, 10);
      dot.classList.remove('he-pf__progress-step--active', 'he-pf__progress-step--completed');
      if (dotStep === stepNum) {
        dot.classList.add('he-pf__progress-step--active');
      } else if (dotStep < stepNum || this.currentStep === 'results') {
        dot.classList.add('he-pf__progress-step--completed');
      }
    });
  }

  async showResults() {
    this.goToStep('results');

    const { goal, method, methodLabel, strengthTag } = this.selections;

    // Update summary text
    const summary = this.querySelector('[data-pf-summary]');
    if (summary) {
      const parts = [`${goal} products`, `in ${methodLabel}`];
      if (strengthTag) parts.push(strengthTag === 'thcp' ? '(Extra Strength)' : '(Regular Strength)');
      summary.textContent = `Showing ${parts.join(' ')}`;
    }

    // Update View All link
    const methodLabelEl = this.querySelector('[data-pf-method-label]');
    if (methodLabelEl) methodLabelEl.textContent = methodLabel;

    const viewAll = this.querySelector('[data-pf-view-all]');
    if (viewAll) viewAll.href = `/collections/${method}`;

    // Show loading
    const loading = this.querySelector('[data-pf-loading]');
    const resultsGrid = this.querySelector('[data-pf-results]');
    const cta = this.querySelector('[data-pf-cta]');

    if (loading) loading.style.display = '';
    if (resultsGrid) resultsGrid.innerHTML = '';
    if (cta) cta.style.display = 'none';

    try {
      const products = await this.fetchProducts(method, goal, strengthTag);
      if (loading) loading.style.display = 'none';

      if (products.length === 0) {
        resultsGrid.innerHTML = `
          <div class="he-pf__no-results" style="grid-column: 1 / -1;">
            <p>No exact matches found. <a href="/collections/${method}">Browse all ${methodLabel}</a></p>
          </div>
        `;
      } else {
        resultsGrid.innerHTML = products.slice(0, 3).map(p => this.renderProductCard(p)).join('');
        if (cta) cta.style.display = '';
        this.wireAddToCartButtons(resultsGrid);
      }
    } catch (err) {
      if (loading) loading.style.display = 'none';
      resultsGrid.innerHTML = `
        <div class="he-pf__no-results" style="grid-column: 1 / -1;">
          <p>Something went wrong. <a href="/collections/${method}">Browse ${methodLabel} instead</a></p>
        </div>
      `;
    }
  }

  async fetchProducts(collection, goal, strengthTag) {
    const url = `/collections/${collection}/products.json?limit=50`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    let products = data.products || [];

    // Filter by strength tag if selected
    if (strengthTag) {
      const tagged = products.filter(p =>
        p.tags && p.tags.some(t => t.toLowerCase().includes(strengthTag.toLowerCase()))
      );
      if (tagged.length > 0) products = tagged;
    }

    // Score products by goal match (check title, tags, and body for goal keyword)
    const goalLower = goal.toLowerCase();
    products = products.map(p => {
      let score = 0;
      const searchText = [
        p.title,
        (p.tags || []).join(' '),
        p.body_html || ''
      ].join(' ').toLowerCase();

      if (searchText.includes(goalLower)) score += 10;

      // Check variant names/options for effect matches
      if (p.variants) {
        p.variants.forEach(v => {
          const variantText = [v.title, ...(v.option_values || []).map(o => o.value)].join(' ').toLowerCase();
          if (variantText.includes(goalLower)) score += 5;
        });
      }

      return { ...p, _score: score };
    });

    // Sort: matched products first, then by title
    products.sort((a, b) => b._score - a._score || a.title.localeCompare(b.title));

    return products;
  }

  renderProductCard(product) {
    const img = product.images && product.images[0]
      ? product.images[0].src
      : 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"/>';
    const firstVariant = product.variants && product.variants[0];
    const price = firstVariant
      ? `$${(parseFloat(firstVariant.price)).toFixed(2)}`
      : '';
    const url = `/products/${product.handle}`;
    const variantId = firstVariant ? firstVariant.id : '';
    const available = firstVariant ? firstVariant.available !== false : true;
    const hasMultipleVariants = product.variants && product.variants.length > 1;

    // If multiple variants, the "Add" button routes to the PDP so the shopper can pick
    const addLabel = hasMultipleVariants ? 'Choose Options' : 'Add to Cart';
    const addAria = hasMultipleVariants ? 'Choose options for ' : 'Add to cart: ';

    return `
      <div class="he-pf__product">
        <a href="${url}" class="he-pf__product-link" aria-label="${this.escapeHtml(product.title)}">
          <img
            class="he-pf__product-img"
            src="${img.replace(/(\.\w+)\?/, '$1?width=400&')}"
            alt="${this.escapeHtml(product.title)}"
            loading="lazy"
            width="400"
            height="400"
          >
          <div class="he-pf__product-info">
            <span class="he-pf__product-title">${this.escapeHtml(product.title)}</span>
            <span class="he-pf__product-price">${price}</span>
          </div>
        </a>
        <div class="he-pf__product-actions">
          <a href="${url}" class="he-pf__product-btn he-pf__product-btn--view">View Product</a>
          ${available
            ? (hasMultipleVariants
                ? `<a href="${url}" class="he-pf__product-btn he-pf__product-btn--add" aria-label="${addAria}${this.escapeHtml(product.title)}">${addLabel}</a>`
                : `<button type="button" class="he-pf__product-btn he-pf__product-btn--add" data-pf-add-to-cart data-variant-id="${variantId}" aria-label="${addAria}${this.escapeHtml(product.title)}">${addLabel}</button>`
              )
            : `<span class="he-pf__product-btn he-pf__product-btn--add he-pf__product-btn--disabled" aria-disabled="true">Sold Out</span>`
          }
        </div>
      </div>
    `;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  wireAddToCartButtons(scope) {
    const buttons = scope.querySelectorAll('[data-pf-add-to-cart]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  async handleAddToCart(event) {
    const btn = event.currentTarget;
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId || btn.disabled) return;
    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      if (!window.HE || typeof window.HE.cartAdd !== 'function') {
        throw new Error('HE.cartAdd helper not loaded');
      }
      // Uses the shared helper so /cart/add.js includes `sections` and the
      // dispatched cart:update has the full payload component-cart-items.js needs.
      await window.HE.cartAdd([{ id: variantId, quantity: 1 }], { source: 'product-finder' });
      btn.textContent = 'Added ✓';
      btn.classList.add('he-pf__product-btn--success');
      window.setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalLabel;
        btn.classList.remove('he-pf__product-btn--success');
      }, 1800);
    } catch (err) {
      btn.textContent = 'Try again';
      window.setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalLabel;
      }, 1500);
    }
  }

  restart() {
    this.selections = { goal: null, method: null, methodLabel: null, strength: null, strengthTag: null };
    this.querySelectorAll('.he-pf__card--selected').forEach(c => c.classList.remove('he-pf__card--selected'));
    this.goToStep(0);
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinder);
}
